// Client-Zugänge für den WireGuard-Konzentrator: Schlüsselpaar, Adresse,
// fertige Konfiguration.
//
// Das Schlüsselpaar entsteht **im Browser** und der private Teil verlässt ihn
// nie — er geht weder an Spyship noch in die Datenbank noch in ein Log. Der
// Server bekommt ausschließlich den öffentlichen Teil, und das ist auch alles,
// was er braucht (docs/wireguard-server.md).
//
// Der Preis dafür: der private Schlüssel existiert genau einmal, im offenen
// Dialog. Wer ihn wegklickt, ohne ihn gesichert zu haben, braucht einen neuen
// Zugang. Das ist die richtige Richtung des Kompromisses — ein Geheimnis, das
// nur beim Empfänger liegt, kann uns niemand abnehmen.

import type { WgOverview, WgServerPeer } from "./api";

export interface WgKeyPair {
  publicKey: string;
  privateKey: string;
  /** Zweites, symmetrisches Geheimnis. Beide Seiten brauchen es — anders als
   *  der private Schlüssel geht es deshalb an den Server. */
  presharedKey: string;
}

/** Fehlt X25519 im Browser, ist das kein Bug, sondern eine Aussage. */
export class NoWebCryptoError extends Error {
  constructor() {
    super(
      "Dieser Browser kann keine X25519-Schlüssel erzeugen. " +
        "Schlüssel extern anlegen (`wg genkey | tee privatekey | wg pubkey`) " +
        "und den öffentlichen Teil hier eintragen.",
    );
    this.name = "NoWebCryptoError";
  }
}

function toBase64(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s);
}

/**
 * X25519-Schlüsselpaar im Format, das WireGuard erwartet: 32 rohe Bytes,
 * base64.
 *
 * `exportKey("pkcs8")` liefert 48 Bytes — 16 Bytes ASN.1-Vorspann plus den
 * Schlüssel. WireGuard will nur den Schlüssel, deshalb die letzten 32.
 */
export async function generateKeyPair(): Promise<WgKeyPair> {
  let pair: CryptoKeyPair;
  try {
    pair = await crypto.subtle.generateKey({ name: "X25519" }, true, ["deriveBits"]);
  } catch {
    throw new NoWebCryptoError();
  }
  const pub = new Uint8Array(await crypto.subtle.exportKey("raw", pair.publicKey));
  const pkcs8 = new Uint8Array(await crypto.subtle.exportKey("pkcs8", pair.privateKey));
  // `wg genpsk` ist nichts weiter als 32 Zufallsbytes in base64.
  const psk = crypto.getRandomValues(new Uint8Array(32));
  return {
    publicKey: toBase64(pub),
    privateKey: toBase64(pkcs8.slice(-32)),
    presharedKey: toBase64(psk),
  };
}

/** Alle Netze, die über den Tunnel erreichbar sind — der Peer-Bestand weiß es.
 *
 *  Genau der Grund, warum diese Konfiguration hier entsteht und nicht von Hand:
 *  ein neuer Kollege soll die Standortnetze erreichen, ohne dass ihm jemand
 *  eine Liste zusammensucht, die beim nächsten Standort schon veraltet ist. */
export function tunnelNetworks(data: WgOverview): string[] {
  const netze = new Set<string>();
  const server = data.server.address;
  if (server) netze.add(networkOf(server));
  for (const peer of data.peers) for (const netz of peer.routes) netze.add(netz);
  return [...netze].sort();
}

/** `10.88.0.250/16` → `10.88.0.0/16`. Nur für die üblichen Präfixe (/8, /16, /24). */
function networkOf(cidr: string): string {
  const [ip, praefix] = cidr.split("/");
  const teile = ip.split(".");
  const behalten = Math.floor(Number(praefix || 32) / 8);
  for (let i = behalten; i < 4; i++) teile[i] = "0";
  return `${teile.join(".")}/${praefix}`;
}

/**
 * Nächste freie Adresse im /24 des Servers.
 *
 * Menschen bekommen ihre Adresse dort (10.88.0.243…247), Standorte liegen in
 * eigenen Netzen. Ein Vorschlag, kein Zwang — überschreiben geht.
 */
export function nextFreeAddress(peers: WgServerPeer[], serverAddress: string): string {
  const basis = serverAddress.split("/")[0].split(".").slice(0, 3).join(".");
  const belegt = new Set(peers.map((p) => p.address.split("/")[0]));
  belegt.add(serverAddress.split("/")[0]);
  // Von oben nach unten: die kleinen Nummern gehören historisch den Standorten,
  // die hohen den Personen. Wer hier eine 2 vergäbe, kollidierte irgendwann mit
  // einem Standort, den es noch nicht gibt.
  for (let i = 254; i >= 200; i--) {
    const kandidat = `${basis}.${i}`;
    if (!belegt.has(kandidat)) return `${kandidat}/32`;
  }
  return "";
}

/** Die fertige `wg-quick`-Konfiguration für den Client. */
export function clientConfig(opts: {
  name: string;
  privateKey: string;
  presharedKey?: string;
  address: string;
  data: WgOverview;
}): string {
  const { server } = opts.data;
  const zeilen = [
    `# ${opts.name} — Eulektro WireGuard`,
    "# Erzeugt in Spyship. Der private Schlüssel steht NUR hier.",
    "",
    "[Interface]",
    `PrivateKey = ${opts.privateKey}`,
    `Address = ${opts.address}`,
  ];
  // Die MTU misst der Sync-Dienst am Interface. Weicht der Client ab, merkt er
  // davon beim Handshake nichts — erst ein großes Paket verschwindet dann.
  if (server.mtu) zeilen.push(`MTU = ${server.mtu}`);
  zeilen.push(
    "",
    "[Peer]",
    `PublicKey = ${server.publicKey ?? "<Server meldet sich nicht>"}`,
    ...(opts.presharedKey ? [`PresharedKey = ${opts.presharedKey}`] : []),
    `Endpoint = ${server.endpoint}`,
    `AllowedIPs = ${tunnelNetworks(opts.data).join(", ")}`,
    // Clientseitig, nicht serverseitig: den Tunnel durch ein Mobilfunk-NAT hält
    // die Seite offen, die dahinter sitzt. Der Konzentrator hat eine feste
    // Adresse und braucht das nicht.
    "PersistentKeepalive = 25",
    "",
  );
  return zeilen.join("\n");
}
